import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useStores } from '../../../stores';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Select,
  TimePicker,
  message,
  Spin,
  Tag,
  Space,
  Empty,
  Popconfirm
} from 'antd';
import {
  PlusOutlined,
  ArrowLeftOutlined,
  DeleteOutlined,
  EditOutlined,
  ClockCircleOutlined,
  CopyOutlined
} from '@ant-design/icons';
import { Checkbox } from 'antd';
import dayjs from 'dayjs';
import './Services.scss';

const { Option } = Select;

const ServiceAvailability = observer(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { partnerServiceStore } = useStores();

  const DAYS_OF_WEEK = [
    { value: 0, label: t('partner.availability.days.monday') },
    { value: 1, label: t('partner.availability.days.tuesday') },
    { value: 2, label: t('partner.availability.days.wednesday') },
    { value: 3, label: t('partner.availability.days.thursday') },
    { value: 4, label: t('partner.availability.days.friday') },
    { value: 5, label: t('partner.availability.days.saturday') },
    { value: 6, label: t('partner.availability.days.sunday') }
  ];
  const { currentService, loading } = partnerServiceStore;

  const [modalVisible, setModalVisible] = useState(false);
  const [editingAvailability, setEditingAvailability] = useState(null);
  const [copyModalVisible, setCopyModalVisible] = useState(false);
  const [copySourceDay, setCopySourceDay] = useState(null);
  const [copyTargetDays, setCopyTargetDays] = useState([]);
  const [copying, setCopying] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    partnerServiceStore.fetchServiceById(id);
  }, [id, partnerServiceStore]);

  const handleAddAvailability = () => {
    setEditingAvailability(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEditAvailability = (availability) => {
    setEditingAvailability(availability);
    form.setFieldsValue({
      day_of_week: availability.day_of_week,
      start_time: dayjs(availability.start_time, 'HH:mm'),
      end_time: dayjs(availability.end_time, 'HH:mm')
    });
    setModalVisible(true);
  };

  const handleDeleteAvailability = async (availabilityId) => {
    const result = await partnerServiceStore.deleteAvailability(id, availabilityId);
    if (result.success) {
      message.success(t('partner.availability.messages.deleteSuccess'));
      // Refresh service data to show updated availability list
      await partnerServiceStore.fetchServiceById(id);
    } else {
      message.error(result.error);
    }
  };

  const handleSubmit = async (values) => {
    const data = {
      day_of_week: values.day_of_week,
      start_time: values.start_time.format('HH:mm'),
      end_time: values.end_time.format('HH:mm')
    };

    // Validation: end_time must be after start_time
    if (values.end_time.isBefore(values.start_time) || values.end_time.isSame(values.start_time)) {
      message.error(t('partner.availability.messages.endTimeAfterStart'));
      return;
    }

    const result = editingAvailability
      ? await partnerServiceStore.updateAvailability(id, editingAvailability.id, data)
      : await partnerServiceStore.addAvailability(id, data);

    if (result.success) {
      message.success(editingAvailability ? t('partner.availability.messages.updateSuccess') : t('partner.availability.messages.addSuccess'));
      setModalVisible(false);
      form.resetFields();
      // Refresh service data to show updated availability list
      await partnerServiceStore.fetchServiceById(id);
    } else {
      message.error(result.error);
    }
  };

  const getDayName = (dayNumber) => {
    const day = DAYS_OF_WEEK.find(d => d.value === dayNumber);
    return day ? day.label : dayNumber;
  };

  // Get unique days that have availability configured
  const getConfiguredDays = () => {
    if (!currentService?.availabilities) return [];
    const days = [...new Set(currentService.availabilities.map(a => a.day_of_week))];
    return days.sort((a, b) => a - b);
  };

  const handleOpenCopyModal = () => {
    setCopySourceDay(null);
    setCopyTargetDays([]);
    setCopyModalVisible(true);
  };

  const handleCopyAvailability = async () => {
    if (copySourceDay === null) {
      message.error(t('partner.availability.messages.selectSourceDay'));
      return;
    }
    if (copyTargetDays.length === 0) {
      message.error(t('partner.availability.messages.selectTargetDay'));
      return;
    }

    setCopying(true);
    const result = await partnerServiceStore.copyAvailability(id, copySourceDay, copyTargetDays);
    setCopying(false);

    if (result.success) {
      message.success(t('partner.availability.messages.copySuccess', { created: result.data.created, skipped: result.data.skipped }));
      setCopyModalVisible(false);
      await partnerServiceStore.fetchServiceById(id);
    } else {
      message.error(result.error);
    }
  };

  const columns = [
    {
      title: t('partner.availability.dayOfWeek'),
      dataIndex: 'day_of_week',
      key: 'day_of_week',
      render: (day) => <Tag color="blue">{getDayName(day)}</Tag>,
      sorter: (a, b) => a.day_of_week - b.day_of_week
    },
    {
      title: t('partner.availability.startTime'),
      dataIndex: 'start_time',
      key: 'start_time',
      render: (time) => (
        <Space>
          <ClockCircleOutlined />
          {time}
        </Space>
      )
    },
    {
      title: t('partner.availability.endTime'),
      dataIndex: 'end_time',
      key: 'end_time',
      render: (time) => (
        <Space>
          <ClockCircleOutlined />
          {time}
        </Space>
      )
    },
    {
      title: t('partner.availability.duration'),
      key: 'duration',
      render: (_, record) => {
        const start = dayjs(record.start_time, 'HH:mm');
        const end = dayjs(record.end_time, 'HH:mm');
        const hours = end.diff(start, 'hour', true);
        return `${hours} ${t('common.hours')}`;
      }
    },
    {
      title: t('common.actions'),
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEditAvailability(record)}
          >
            {t('common.edit')}
          </Button>
          <Popconfirm
            title={t('partner.availability.deleteTitle')}
            description={t('partner.availability.deleteConfirm')}
            onConfirm={() => handleDeleteAvailability(record.id)}
            okText={t('common.delete')}
            okType="danger"
            cancelText={t('common.cancel')}
          >
            <Button type="text" danger icon={<DeleteOutlined />}>
              {t('common.delete')}
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  if (loading) {
    return (
      <div className="loading-container">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="service-availability">
      <div className="page-header">
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/partner/services')}
        >
          {t('partner.services.backToServices')}
        </Button>
        <h1>{t('partner.availability.manageAvailability')}</h1>
      </div>

      {currentService && (
        <Card className="service-info-card">
          <h2>{currentService.name_en}</h2>
          <p>{currentService.name_el}</p>
          <Tag color="blue">{currentService.service_category_name}</Tag>
        </Card>
      )}

      <Card
        title={t('partner.availability.workingHours')}
        extra={
          <Space>
            {getConfiguredDays().length > 0 && (
              <Button
                icon={<CopyOutlined />}
                onClick={handleOpenCopyModal}
              >
                {t('partner.availability.copyToOtherDays')}
              </Button>
            )}
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddAvailability}
            >
              {t('partner.availability.addAvailability')}
            </Button>
          </Space>
        }
      >
        {currentService?.availabilities && currentService.availabilities.length > 0 ? (
          <Table
            columns={columns}
            dataSource={currentService.availabilities}
            rowKey="id"
            pagination={false}
          />
        ) : (
          <Empty
            description={t('partner.availability.noSlots')}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          >
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAddAvailability}>
              {t('partner.availability.addFirstSlot')}
            </Button>
          </Empty>
        )}
      </Card>

      <Modal
        title={editingAvailability ? t('partner.availability.editAvailability') : t('partner.availability.addAvailability')}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="day_of_week"
            label={t('partner.availability.dayOfWeek')}
            rules={[{ required: true, message: t('partner.availability.selectDay') }]}
          >
            <Select placeholder={t('partner.availability.selectDayPlaceholder')}>
              {DAYS_OF_WEEK.map(day => (
                <Option key={day.value} value={day.value}>{day.label}</Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="start_time"
            label={t('partner.availability.startTime')}
            rules={[{ required: true, message: t('partner.availability.selectStartTime') }]}
          >
            <TimePicker
              format="HH:mm"
              minuteStep={15}
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item
            name="end_time"
            label={t('partner.availability.endTime')}
            rules={[{ required: true, message: t('partner.availability.selectEndTime') }]}
          >
            <TimePicker
              format="HH:mm"
              minuteStep={15}
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingAvailability ? t('common.update') : t('common.add')}
              </Button>
              <Button onClick={() => {
                setModalVisible(false);
                form.resetFields();
              }}>
                {t('common.cancel')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Copy Availability Modal */}
      <Modal
        title={t('partner.availability.copyTitle')}
        open={copyModalVisible}
        onCancel={() => setCopyModalVisible(false)}
        onOk={handleCopyAvailability}
        okText={t('partner.availability.copy')}
        cancelText={t('common.cancel')}
        okButtonProps={{ loading: copying, disabled: copySourceDay === null || copyTargetDays.length === 0 }}
      >
        <div style={{ marginBottom: 16 }}>
          <p><strong>{t('partner.availability.step1')}:</strong> {t('partner.availability.selectSourceDay')}:</p>
          <Select
            placeholder={t('partner.availability.selectSourceDayPlaceholder')}
            style={{ width: '100%' }}
            value={copySourceDay}
            onChange={(value) => {
              setCopySourceDay(value);
              // Remove source day from target days if selected
              setCopyTargetDays(copyTargetDays.filter(d => d !== value));
            }}
          >
            {getConfiguredDays().map(dayNum => (
              <Option key={dayNum} value={dayNum}>
                {getDayName(dayNum)} ({currentService?.availabilities?.filter(a => a.day_of_week === dayNum).length} {t('partner.availability.slots')})
              </Option>
            ))}
          </Select>
        </div>

        <div>
          <p><strong>{t('partner.availability.step2')}:</strong> {t('partner.availability.selectTargetDays')}:</p>
          <Checkbox.Group
            value={copyTargetDays}
            onChange={setCopyTargetDays}
            style={{ width: '100%' }}
          >
            <Space direction="vertical">
              {DAYS_OF_WEEK.filter(d => d.value !== copySourceDay).map(day => (
                <Checkbox key={day.value} value={day.value}>
                  {day.label}
                </Checkbox>
              ))}
            </Space>
          </Checkbox.Group>
        </div>
      </Modal>
    </div>
  );
});

export default ServiceAvailability;
